import { getFirst, listGet } from 'utils/array';
import { Cost, FullProject, ProjectService } from '../types';
import { calculateProjectTotal, getCalculationReference } from './projectTotal';
import { CostCalculation } from 'entities/modules/teams';

// x / y => x is Dividend, y is Divisor
/**
 * We know this is a flawed resolution if CostCalculation[] is complex
 *
 * For our v1, however, we know in advance our services work with 'AS_TOTAL' and 'MULTIPLICATION' tops
 *
 * We are interested in getting the feature out for that use case, as the probability of having a complex service
 * calculation in the near future is almost 0.
 *
 * If we do, however, our managers get notified as soon as a service is created/updated to verify if we need to act
 * upon the new calc introduced
 */
function hasMultiplicationDividend(
  calculationRoot: CostCalculation,
  calculation: CostCalculation[],
) {
  const { operation, base } = calculationRoot;

  if (operation === 'MULTIPLY') return true;

  if (operation === 'AS_TOTAL' && base.type === 'CALCULATION')
    return hasMultiplicationDividend(listGet(calculation, base.ref)!, calculation);

  return false;
}

function getMultiplicationDivided(
  calculationRoot: CostCalculation,
  calculation: CostCalculation[],
  costs: Cost[],
) {
  const { operation, base, value } = calculationRoot;

  if (operation === 'AS_TOTAL' && base.type === 'CALCULATION')
    return getMultiplicationDivided(listGet(calculation, base.ref)!, calculation, costs);

  if (operation !== 'MULTIPLY') return 0;

  const numericRef = [base.ref, value!.ref]
    .map((variableId) => listGet(costs, variableId)!)
    .find((variable) => variable.type === 'NUMBER');

  return Number(numericRef?.value) || 0;
}

function hasCountMultiplication(service: ProjectService) {
  const costsRef = getFirst(Object.values(service.costs));

  // the final result for the service price
  const calculationRef = getCalculationReference({
    ...service,
    costs: costsRef,
  });

  if (!calculationRef) return false;

  return hasMultiplicationDividend(calculationRef, service.costCalculation);
}

function getServiceCPICount(service: ProjectService) {
  if (!hasCountMultiplication(service)) return 0;

  const count = Object.values(service.costs).reduce(
    (acc, costs) =>
      acc +
      getMultiplicationDivided(
        getCalculationReference({ ...service, costs })!,
        service.costCalculation,
        costs,
      ),
    0,
  );

  return count;
}

export function calculateProjectCPI(services: ProjectService[]) {
  const total = calculateProjectTotal(services);

  const servicesToCount = services.filter(hasCountMultiplication);

  if (!servicesToCount.length) return total;

  const count = servicesToCount.reduce((acc, next) => acc + getServiceCPICount(next), 0);

  if (!count) return total;

  return (Number(total) / count).toFixed(2);
}

export function shouldShowProjectCPI({ tasks, services }: FullProject) {
  return tasks.some((t) => t.canCreateSampleProject) || services.some(hasCountMultiplication);
}
